import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Loader2, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthPage = ({ onBack }) => {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');  // set when an account needs email confirmation

  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    
    try {
      if (mode === 'login') {
        await login(email, password);
      } else if (mode === 'register') {
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match');
        }
        if (password.length < 6) {
          throw new Error('Password must be at least 6 characters');
        }
        const res = await register(email, password, confirmPassword, fullName, companyName);
        setMessage(res?.message || 'Account created. Check your email to confirm your address before signing in.');
        setPendingEmail(email);
        setMode('login');
        setPassword('');
        setConfirmPassword('');
      } else if (mode === 'forgot') {
        const response = await axios.post(`${API}/auth/forgot-password`, { email });
        setMessage('If an account exists with this email, check your inbox for reset instructions.');
        if (response.data.reset_token) {
          setResetToken(response.data.reset_token);
          setMode('reset');
        }
      } else if (mode === 'reset') {
        if (newPassword.length < 6) {
          throw new Error('Password must be at least 6 characters');
        }
        await axios.post(`${API}/auth/reset-password`, { 
          token: resetToken, 
          new_password: newPassword 
        });
        setMessage('Password reset successfully! You can now login.');
        setMode('login');
        setResetToken('');
      }
    } catch (err) {
      // A 403 on login means the email isn't confirmed yet — surface the resend link.
      if (err.response?.status === 403) {
        setPendingEmail(email);
      }
      setError(err.response?.data?.detail || err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setMessage('');
    try {
      const res = await axios.post(`${API}/auth/resend-confirmation`, { email: pendingEmail || email });
      setMessage(res.data?.message || "If an unconfirmed account exists for this email, we've resent the confirmation link.");
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not resend the confirmation email. Please try again.');
    }
  };

  const renderPasswordInput = (value, setValue, placeholder, show, setShow, testId) => (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="input pr-10"
        placeholder={placeholder}
        required
        minLength={6}
        data-testid={testId}
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-muted)] hover:text-[var(--color-charcoal)]"
        tabIndex={-1}
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-[var(--color-bg)]">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-[var(--color-muted)] hover:text-[var(--color-charcoal)] mb-8 transition-colors text-sm"
          data-testid="auth-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </button>

        {/* Logo */}
        <div className="flex items-center gap-2 mb-10">
          <img src="/stilos-logo.png" alt="Stilos" className="w-9 h-9 rounded-lg object-cover" />
          <span className="font-display font-semibold text-lg tracking-tight">Stilos</span>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl mb-2">
            {mode === 'login' && 'Welcome back'}
            {mode === 'register' && 'Start building pillars'}
            {mode === 'forgot' && 'Reset your password'}
            {mode === 'reset' && 'Create new password'}
          </h1>
          <p className="text-sm text-[var(--color-muted)]">
            {mode === 'login' && 'Sign in to continue creating content that AI trusts.'}
            {mode === 'register' && 'Create your account and get 3 free articles + 10 credits.'}
            {mode === 'forgot' && "Enter your email and we'll send you reset instructions."}
            {mode === 'reset' && 'Enter your new password below.'}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 rounded-xl text-sm bg-red-50 text-red-700" data-testid="auth-error">
            {error}
          </div>
        )}

        {/* Success Message */}
        {message && (
          <div className="mb-6 p-4 rounded-xl text-sm bg-green-50 text-green-700">
            {message}
          </div>
        )}

        {/* Resend confirmation — shown in the check-your-email state */}
        {pendingEmail && mode === 'login' && (
          <div className="mb-6 text-center text-sm">
            <span className="text-[var(--color-muted)]">Didn't get the confirmation email? </span>
            <button
              type="button"
              onClick={handleResend}
              className="font-medium text-[var(--color-coral)] hover:underline"
              data-testid="auth-resend-btn"
            >
              Resend it
            </button>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <>
              <div>
                <label className="block text-xs font-medium mb-1.5 text-[var(--color-charcoal)]">Full Name *</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="input"
                  placeholder="John Doe"
                  required
                  data-testid="auth-fullname-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5 text-[var(--color-charcoal)]">Company Name</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="input"
                  placeholder="Acme Inc."
                  data-testid="auth-company-input"
                />
              </div>
            </>
          )}
          
          {(mode === 'login' || mode === 'register' || mode === 'forgot') && (
            <div>
              <label className="block text-xs font-medium mb-1.5 text-[var(--color-charcoal)]">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="you@example.com"
                required
                data-testid="auth-email-input"
              />
            </div>
          )}
          
          {(mode === 'login' || mode === 'register') && (
            <div>
              <label className="block text-xs font-medium mb-1.5 text-[var(--color-charcoal)]">Password</label>
              {renderPasswordInput(password, setPassword, 'Enter your password', showPassword, setShowPassword, 'auth-password-input')}
              {mode === 'register' && (
                <p className="text-[10px] text-[var(--color-muted)] mt-1">Must be at least 6 characters</p>
              )}
            </div>
          )}

          {mode === 'register' && (
            <div>
              <label className="block text-xs font-medium mb-1.5 text-[var(--color-charcoal)]">Confirm Password</label>
              {renderPasswordInput(confirmPassword, setConfirmPassword, 'Confirm your password', showConfirmPassword, setShowConfirmPassword, 'auth-confirm-password-input')}
              {password && confirmPassword && password !== confirmPassword && (
                <p className="text-[10px] mt-1 text-[var(--color-coral)]">Passwords do not match</p>
              )}
            </div>
          )}

          {mode === 'reset' && (
            <>
              <div>
                <label className="block text-xs font-medium mb-1.5 text-[var(--color-charcoal)]">Reset Token</label>
                <input
                  type="text"
                  value={resetToken}
                  onChange={(e) => setResetToken(e.target.value)}
                  className="input font-mono text-sm"
                  placeholder="Paste your reset token"
                  required
                  data-testid="auth-reset-token-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5 text-[var(--color-charcoal)]">New Password</label>
                {renderPasswordInput(newPassword, setNewPassword, 'Enter new password', showPassword, setShowPassword, 'auth-new-password-input')}
              </div>
            </>
          )}

          {mode === 'login' && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => { setMode('forgot'); setError(''); setMessage(''); }}
                className="text-xs text-[var(--color-coral)] hover:underline"
              >
                Forgot password?
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || (mode === 'register' && password !== confirmPassword)}
            className="btn btn-primary w-full mt-6"
            data-testid="auth-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                {mode === 'login' && 'Sign in'}
                {mode === 'register' && 'Create account'}
                {mode === 'forgot' && 'Send reset link'}
                {mode === 'reset' && 'Reset password'}
              </>
            )}
          </button>
        </form>

        {/* Mode Toggle */}
        <div className="mt-6 text-center text-sm">
          {mode === 'login' && (
            <>
              <span className="text-[var(--color-muted)]">Don't have an account? </span>
              <button
                onClick={() => { setMode('register'); setError(''); setMessage(''); }}
                className="font-medium text-[var(--color-coral)] hover:underline"
                data-testid="auth-toggle-btn"
              >
                Sign up free
              </button>
            </>
          )}
          {mode === 'register' && (
            <>
              <span className="text-[var(--color-muted)]">Already have an account? </span>
              <button
                onClick={() => { setMode('login'); setError(''); setMessage(''); }}
                className="font-medium text-[var(--color-coral)] hover:underline"
                data-testid="auth-toggle-btn"
              >
                Sign in
              </button>
            </>
          )}
          {(mode === 'forgot' || mode === 'reset') && (
            <>
              <span className="text-[var(--color-muted)]">Remember your password? </span>
              <button
                onClick={() => { setMode('login'); setError(''); setMessage(''); }}
                className="font-medium text-[var(--color-coral)] hover:underline"
              >
                Sign in
              </button>
            </>
          )}
        </div>

        {/* Free tier info */}
        {mode === 'register' && (
          <div className="mt-8 p-5 rounded-2xl bg-[var(--color-surface)]">
            <p className="text-xs font-semibold mb-2 text-[var(--color-coral)]">What you get for free:</p>
            <ul className="text-xs text-[var(--color-muted)] space-y-1">
              <li>&#8226; 3 AI-generated articles (lifetime)</li>
              <li>&#8226; 10 credits for SEO analysis & chat</li>
              <li>&#8226; Full SEO scoring on every article</li>
              <li>&#8226; No credit card required</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthPage;
